'use client';

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { companyLogosSchema } from '@/lib/company-overview-schemas';
import { getPublicStorageUrl } from '@/lib/public-storage-url';

export const COMPANY_LOGOS_KEY = ['company-logos'] as const;
const responseSchema = z.object({ data: companyLogosSchema });
type LogoItems = z.infer<typeof companyLogosSchema>['items'];

// /api/companies/logos accepts at most 100 eu_ids per call (zod .max(100)).
const CHUNK_SIZE = 100;

async function fetchLogoChunk(ids: string[]): Promise<LogoItems> {
  const res = await fetch('/api/companies/logos', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ eu_ids: ids }),
  });
  if (!res.ok) throw new Error(`Company logos returned ${res.status}`);
  return responseSchema.parse(await res.json()).data.items;
}

export function useCompanyLogos(euIds: string[]) {
  const ids = [...new Set(euIds)].sort();
  const { data } = useQuery({
    queryKey: [...COMPANY_LOGOS_KEY, ...ids],
    queryFn: async () => {
      const chunks: string[][] = [];
      for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
        chunks.push(ids.slice(i, i + CHUNK_SIZE));
      }
      // A failed chunk must not blank the others: merge what succeeded and
      // throw only when every chunk failed.
      const results = await Promise.allSettled(chunks.map(fetchLogoChunk));
      const items: LogoItems = {};
      let firstError: unknown = null;
      let succeeded = 0;
      for (const result of results) {
        if (result.status === 'fulfilled') {
          Object.assign(items, result.value);
          succeeded += 1;
        } else if (firstError === null) {
          firstError = result.reason;
        }
      }
      if (succeeded === 0 && firstError !== null) throw firstError;
      return items;
    },
    staleTime: 6 * 60 * 60_000,
    enabled: ids.length > 0,
  });
  return (euId: string): string | undefined => {
    const item = data?.[euId];
    return (
      getPublicStorageUrl(item?.logo_url) ??
      getPublicStorageUrl(item?.favicon_url)
    );
  };
}
